import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Printer, X } from 'lucide-react';
import { formatCurrency, formatDateTime } from '@/lib/i18n';
import { useApi } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { useDialogEscape } from '@/components/ui/dialog';
import { type Check, balanceOf, fromMinor, toMinor } from './lib';
import './PrintableCheck.css';

/**
 * The bill, on paper.
 *
 * Printing is the browser's own: a dedicated print root plus `@media print`
 * (see `PrintableCheck.css`), the same shape `Pages/Spaces/TableQrSheet.tsx`
 * uses. No PDF library, so no second layout engine to disagree with the screen.
 *
 * ## Where each line comes from
 *
 * `GET /checks/:id` answers the check itself, its `charges`, its `payments` and
 * the `session` it belongs to — which now carries the table's number, because
 * a bill reaches its table through its visit. It still carries no branch, no
 * area and no staff member, so the header is assembled from two further reads
 * that are deliberately **best-effort**:
 *
 * - `GET /branches/:branchId` (`branches.view`) → venue name, code, and
 *   `address` / `phone` when the branch has them. They are nullable columns on
 *   `Branch`, so a venue that never filled them in simply prints without them.
 * - `GET /sessions/:id` (`sessions.view`) → the table's AREA and the waiter,
 *   neither of which the check's own session block carries.
 *
 * A cashier may hold `checks.view` without either of those keys. That prints a
 * narrower bill, not a broken one: a header field with no source is omitted,
 * never filled with a placeholder that a guest would read as fact.
 *
 * Dine-in vs takeaway is **not** printed. `fulfillmentType` lives on `Order`,
 * and a check exposes no orders — a check with no session could be a takeaway
 * or a bar tab, and guessing on a bill is worse than staying quiet.
 *
 * ## Money
 *
 * Every total is a fixed-scale decimal string from the API. Nothing here adds
 * or subtracts with floats: the only arithmetic is the discount's sign flip and
 * the outstanding balance, both through the integer minor units in `./lib`,
 * quantised to the check's own currency. `formatCurrency` then renders three
 * places for KWD and none for JPY.
 */

type Paper = 'roll' | 'a4';

/** Remembered per browser: a venue with a thermal printer picks roll once. */
const PAPER_KEY = 'venue.billPaper';

/** Only the `Branch` columns that belong on a bill. All four really exist. */
interface BranchRow {
  name: string;
  code: string;
  address: string | null;
  phone: string | null;
}

/** From `GET /sessions` — `SESSION_INCLUDE` returns exactly these shapes. */
interface SessionRow {
  id: string;
  table?: {
    number: string;
    name: string | null;
    area?: { name: string } | null;
  } | null;
  waiter?: { firstName: string; lastName: string | null } | null;
}

interface Props {
  check: Check;
  onClose: () => void;
}

export default function PrintableCheck({ check, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const [paper, setPaper] = useState<Paper>(
    () => (localStorage.getItem(PAPER_KEY) as Paper | null) ?? 'roll',
  );
  const [printedAt] = useState(() => new Date().toISOString());

  // Best-effort header data — see the note above. `useApi(null)` never fires,
  // so a caller without the key costs no request and gets no console 403.
  const branch = useApi<BranchRow>(
    can('branches.view') ? `/branches/${check.branchId}` : null,
  );
  const sessionRead = useApi<SessionRow>(
    can('sessions.view') && check.sessionId ? `/sessions/${check.sessionId}` : null,
  );
  const session = sessionRead.data ?? null;

  // While the preview is up the app is print-suppressed, so the paper carries
  // the bill alone. The class lives on <body> because the sheet is portalled
  // there and CSS cannot reach an ancestor from a descendant.
  useEffect(() => {
    document.body.classList.add('bill-printing');
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.classList.remove('bill-printing');
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  /**
   * Escape goes through the shared dialog stack rather than a listener of this
   * component's own. The preview opens *over* the settle dialog and portals to
   * `<body>`, so one press used to close the preview AND the check behind it;
   * registering here means the topmost surface — this one — is the only one that
   * answers.
   */
  const surface = useDialogEscape(onClose);

  const currency = check.currency;
  const money = (amount: string | null | undefined) =>
    formatCurrency(amount ?? '0', currency, i18n.language);
  const isPositive = (amount: string | null | undefined) => toMinor(amount, currency) > 0;
  /** `discountTotal` is a positive magnitude; a bill shows it as a deduction. */
  const asDeduction = (amount: string) => fromMinor(-toMinor(amount, currency), currency);

  const outstanding = balanceOf(check);

  /**
   * A voided charge stays on the ledger (§34) but never appears on the bill,
   * and a DISCOUNT charge is already counted in `discountTotal` — printing it
   * as a line as well would deduct it twice on the paper.
   */
  const lines = (check.charges ?? []).filter(
    (charge) => !charge.voidedAt && charge.type !== 'DISCOUNT',
  );
  /** A failed authorisation is not money the guest handed over. */
  const payments = (check.payments ?? []).filter((payment) => payment.status !== 'FAILED');

  const table = session?.table;
  const waiterName = session?.waiter
    ? [session.waiter.firstName, session.waiter.lastName].filter(Boolean).join(' ')
    : null;

  const choosePaper = (next: Paper) => {
    setPaper(next);
    localStorage.setItem(PAPER_KEY, next);
  };

  return createPortal(
    <div
      ref={surface}
      className="bill-print-root"
      role="dialog"
      aria-modal="true"
      aria-label={t('billing.print.preview')}
    >
      {/*
        `@page` cannot be selected by a class, and named pages are still uneven
        across engines — so the one rule that has to change with the paper is
        written out here instead of living in the stylesheet.
      */}
      <style>
        {paper === 'roll'
          ? '@page { size: 80mm auto; margin: 3mm; }'
          : '@page { size: A4 portrait; margin: 14mm; }'}
      </style>

      <div className="bill-print-toolbar">
        <span className="bill-print-toolbar-title">{t('billing.print.preview')}</span>
        <div className="flex gap-1" role="group" aria-label={t('billing.print.paper')}>
          <Button
            size="sm"
            variant={paper === 'roll' ? 'default' : 'outline'}
            onClick={() => choosePaper('roll')}
          >
            {t('billing.print.paperRoll')}
          </Button>
          <Button
            size="sm"
            variant={paper === 'a4' ? 'default' : 'outline'}
            onClick={() => choosePaper('a4')}
          >
            {t('billing.print.paperA4')}
          </Button>
        </div>
        <Button size="sm" onClick={() => window.print()}>
          <Printer className="h-4 w-4" />
          {t('billing.print.print')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose} aria-label={t('common.close')}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <article className={`bill-sheet bill-sheet--${paper}`}>
        {/* Venue — omitted wholesale when the branch could not be read. */}
        {branch.data && (
          <header className="bill-venue">
            <h1 className="bill-venue-name">{branch.data.name}</h1>
            {branch.data.address && <p className="bill-venue-line">{branch.data.address}</p>}
            {branch.data.phone && (
              <p className="bill-venue-line" dir="ltr">
                {branch.data.phone}
              </p>
            )}
            <p className="bill-venue-line">{branch.data.code}</p>
          </header>
        )}

        <p className="bill-number">{t('billing.checks.checkNumber', { number: check.number })}</p>

        <hr className="bill-rule bill-rule--strong" />

        <dl className="bill-meta">
          {table ? (
            <>
              <dt>{t('billing.print.table')}</dt>
              <dd>{table.name ? `${table.number} · ${table.name}` : table.number}</dd>
              {table.area?.name && (
                <>
                  <dt>{t('billing.print.area')}</dt>
                  <dd>{table.area.name}</dd>
                </>
              )}
            </>
          ) : check.session?.table ? (
            <>
              <dt>{t('billing.print.table')}</dt>
              <dd>{check.session.table.number}</dd>
            </>
          ) : (
            <>
              <dt>{t('billing.print.visit')}</dt>
              <dd>
                {check.session ? t('billing.checks.visitLine') : t('billing.checks.walkIn')}
              </dd>
            </>
          )}

          {waiterName && (
            <>
              <dt>{t('billing.print.server')}</dt>
              <dd>{waiterName}</dd>
            </>
          )}

          <dt>{t('billing.print.opened')}</dt>
          <dd>{formatDateTime(check.openedAt, i18n.language)}</dd>

          {check.closedAt && (
            <>
              <dt>{t('billing.print.closed')}</dt>
              <dd>{formatDateTime(check.closedAt, i18n.language)}</dd>
            </>
          )}

          <dt>{t('common.status')}</dt>
          <dd>{t(`billing.status.${check.status}`)}</dd>
        </dl>

        <hr className="bill-rule" />

        <table className="bill-lines">
          <thead>
            <tr>
              <th className="bill-col-qty">{t('billing.print.qty')}</th>
              <th className="bill-col-desc">{t('billing.print.item')}</th>
              <th className="bill-col-amount">{t('billing.print.amount')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td className="bill-col-qty" />
                <td className="bill-col-desc" colSpan={2}>
                  {t('billing.charges.empty')}
                </td>
              </tr>
            ) : (
              lines.map((charge) => (
                <tr key={charge.id}>
                  <td className="bill-col-qty">{charge.quantity}</td>
                  <td className="bill-col-desc">
                    {charge.description}
                    {/* Unit price only when it adds something a single line does not. */}
                    {isPositive(charge.unitPrice) && (
                      <div className="bill-line-unit">
                        {t('billing.print.unitPrice', { price: money(charge.unitPrice) })}
                      </div>
                    )}
                  </td>
                  <td className="bill-col-amount">{money(charge.amount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <hr className="bill-rule" />

        <dl className="bill-totals">
          <div className="bill-total-row">
            <dt>{t('common.subtotal')}</dt>
            <dd>{money(check.subtotal)}</dd>
          </div>
          {isPositive(check.discountTotal) && (
            <div className="bill-total-row">
              <dt>{t('common.discount')}</dt>
              <dd>{money(asDeduction(check.discountTotal))}</dd>
            </div>
          )}
          {isPositive(check.serviceTotal) && (
            <div className="bill-total-row">
              <dt>{t('billing.checks.service')}</dt>
              <dd>{money(check.serviceTotal)}</dd>
            </div>
          )}
          {isPositive(check.taxTotal) && (
            <div className="bill-total-row">
              <dt>{t('common.tax')}</dt>
              <dd>{money(check.taxTotal)}</dd>
            </div>
          )}
          {isPositive(check.tipTotal) && (
            <div className="bill-total-row">
              <dt>{t('common.tip')}</dt>
              <dd>{money(check.tipTotal)}</dd>
            </div>
          )}
        </dl>

        <hr className="bill-rule bill-rule--strong" />

        <dl className="bill-totals">
          <div className="bill-total-row bill-total-row--grand">
            <dt>{t('billing.print.total')}</dt>
            <dd>{money(check.grandTotal)}</dd>
          </div>
        </dl>

        <hr className="bill-rule bill-rule--dashed" />

        <section>
          <h2 className="bill-section-title">{t('billing.payments.title')}</h2>
          {payments.length === 0 ? (
            <p className="bill-empty">{t('billing.print.noPayments')}</p>
          ) : (
            payments.map((payment) => (
              <div key={payment.id} className="bill-payment">
                <div>
                  {t(`billing.method.${payment.method}`)}
                  {payment.reference && (
                    <span className="bill-payment-note"> · {payment.reference}</span>
                  )}
                  {payment.paidAt && (
                    <div className="bill-payment-note">
                      {formatDateTime(payment.paidAt, i18n.language)}
                    </div>
                  )}
                  {isPositive(payment.tipAmount) && (
                    <div className="bill-payment-note">
                      {t('common.tip')}{' '}
                      {formatCurrency(payment.tipAmount, payment.currency, i18n.language)}
                    </div>
                  )}
                  {toMinor(payment.refundedAmount, payment.currency) > 0 && (
                    <div className="bill-payment-note">
                      {t('billing.payments.refunded')}{' '}
                      {formatCurrency(payment.refundedAmount, payment.currency, i18n.language)}
                    </div>
                  )}
                </div>
                <span>{formatCurrency(payment.amount, payment.currency, i18n.language)}</span>
              </div>
            ))
          )}

          <hr className="bill-rule" />

          <dl className="bill-totals">
            <div className="bill-total-row">
              <dt>{t('billing.checks.paid')}</dt>
              <dd>{money(check.paidTotal)}</dd>
            </div>
            <div className="bill-total-row bill-total-row--outstanding">
              <dt>{t('billing.checks.outstanding')}</dt>
              <dd>{money(outstanding)}</dd>
            </div>
          </dl>
        </section>

        <footer className="bill-footer">
          <p className="bill-footer-thanks">{t('billing.print.thankYou')}</p>
          <p className="bill-footer-printed">
            {t('billing.print.printedAt', { at: formatDateTime(printedAt, i18n.language) })}
          </p>
        </footer>
      </article>
    </div>,
    document.body,
  );
}
